import { useNavigate } from 'react-router-dom'
import { GlassHoverEffect } from '../../../components/GlassHoverEffect'
import {
  HiMiniInboxArrowDown,
  HiOutlinePaperAirplane,
  HiEye,
  HiUserPlus,
  HiOutlineShieldCheck,
} from 'react-icons/hi2'
import { useIsGestorDoSetorLogado } from '../../../hooks/useIsGestorDoSetorLogado'
import { useDemandasAutorizacaoQuery } from '../hooks/queries/useDemandasAutorizacaoQuery'

export type DemandType = 'received' | 'sent' | 'watching' | 'toAssign' | 'authorization'

interface DemandsNavigationProps {
  currentType: DemandType
  onTypeChange: (type: DemandType) => void
}

export const DemandsNavigation = ({ currentType, onTypeChange }: DemandsNavigationProps) => {
  const navigate = useNavigate()
  const { isGestor } = useIsGestorDoSetorLogado()

  // Buscar autorizações pendentes para mostrar indicador
  const { data: autorizacoes = [] } = useDemandasAutorizacaoQuery({ enabled: true })
  const hasAutorizacoesPendentes = autorizacoes.length > 0

  const navigationItems = [
    {
      label: 'Recebidas',
      type: 'received' as DemandType,
      description: isGestor
        ? 'Todas as demandas recebidas do setor'
        : 'Demandas enviadas para você',
      path: '/demands/received',
      icon: <HiMiniInboxArrowDown className='size-5' />,
      showBadge: false,
    },
    {
      label: 'Enviadas',
      type: 'sent' as DemandType,
      description: isGestor ? 'Todas as demandas enviadas do setor' : 'Demandas que você criou',
      path: '/demands/sent',
      icon: <HiOutlinePaperAirplane className='size-5' />,
      showBadge: false,
    },
    {
      label: 'Observáveis',
      type: 'watching' as DemandType,
      description: 'Demandas que você está acompanhando',
      path: '/demands/watching',
      icon: <HiEye className='size-4' />,
      showBadge: false,
    },
    {
      label: 'Em Atribuição',
      type: 'toAssign' as DemandType,
      description: 'Demandas que chegaram ao setor e ainda não foram atribuídas.',
      path: '/demands/toAssign',
      icon: <HiUserPlus className='size-5' />,
      showBadge: false,
    },
  ]

  // Sempre adicionar opção de Autorizações (mostra todas do setor aguardando autorização)
  navigationItems.push({
    label: 'Autorizações',
    type: 'authorization' as DemandType,
    description: 'Demandas do setor aguardando autorização',
    path: '/demands/authorization',
    icon: <HiOutlineShieldCheck className='size-5' />,
    showBadge: hasAutorizacoesPendentes,
  })

  const handleTypeChange = (type: DemandType) => {
    onTypeChange(type)
    const item = navigationItems.find(item => item.type === type)
    if (item) {
      navigate(item.path)
    }
  }

  // Calculate active index based on current type
  const activeIndex = navigationItems.findIndex(item => item.type === currentType)

  return (
    <div className='flex justify-start'>
      <GlassHoverEffect activeIndex={activeIndex} direction='horizontal'>
        {({ containerRef, containerProps, getItemProps }) => (
          <nav
            ref={containerRef}
            className='bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-0.5 flex space-x-0.5 border border-gray-200 dark:border-neutral-700'
            {...containerProps}
          >
            {navigationItems.map((item, index) => (
              <button
                key={item.type}
                {...getItemProps(index)}
                onClick={() => handleTypeChange(item.type)}
                className={`
                  px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 relative z-10 min-w-[120px] justify-center
                  ${
                    currentType === item.type
                      ? 'text-blue-700 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-neutral-700'
                  }
                `}
              >
                <span className='relative'>
                  {item.icon}
                  {/* Badge de notificação */}
                  {item.showBadge && (
                    <span
                      className='absolute -top-1 -right-21 w-2 h-2 bg-red-500 rounded-full'
                      aria-label='Há autorizações pendentes'
                    />
                  )}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        )}
      </GlassHoverEffect>
    </div>
  )
}
